/**
 * Affichage des cartes de masquage en séance (design system, MasquageImage, MasquageTableau).
 * L'état de chaque zone ou case découle du mode, de la face et de la zone demandée.
 */
import { useContext, useEffect, useState } from 'react';
import type { ContenuImage, ContenuTableau, ModeMasquage } from '../../cartes/carte-mere';
import { rendreEnLigneMarkdown } from '../contenu/ContenuDeCarte';
import { ContexteImages } from '../contenu/ContenuDeCarte';

type Etat = 'masque' | 'demande' | 'revele' | null;

/** État d'une zone ou d'une case grisée (`demandee` : celle de la carte fille, ou toutes). */
export function etatMasquage(
  mode: ModeMasquage,
  face: 'recto' | 'verso',
  estDemandee: boolean,
): Etat {
  if (mode === 'tout_masquer_tout_demander') return face === 'recto' ? 'demande' : 'revele';
  if (estDemandee) return face === 'recto' ? 'demande' : 'revele';
  return mode === 'tout_masquer_une_demandee' ? 'masque' : null;
}

export function MasquageImage({
  contenu,
  mode,
  face,
  zoneDemandee,
}: {
  contenu: ContenuImage;
  mode: ModeMasquage;
  face: 'recto' | 'verso';
  zoneDemandee: string | null;
}) {
  const resoudre = useContext(ContexteImages);
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let actif = true;
    void resoudre(contenu.image)
      .then((s) => actif && setSrc(s))
      .catch(() => undefined);
    return () => {
      actif = false;
    };
  }, [contenu.image, resoudre]);
  return (
    <figure className="fc-masquage-image">
      <div className="fc-masquage-image__cadre">
        {src ? (
          <img className="fc-masquage-image__image" src={src} alt="Image de la carte" />
        ) : (
          <div className="fc-masquage-image__image" style={{ aspectRatio: '4 / 3' }} />
        )}
        {contenu.zones.map((zone) => {
          const etat = etatMasquage(mode, face, zone.id === zoneDemandee);
          if (etat === null) return null;
          return (
            <div
              key={zone.id}
              className="fc-masquage-image__zone"
              data-etat={etat}
              role="img"
              aria-label={
                etat === 'demande'
                  ? 'Zone demandée'
                  : etat === 'revele'
                    ? 'Zone révélée'
                    : 'Zone masquée'
              }
              style={{
                left: `${zone.x * 100}%`,
                top: `${zone.y * 100}%`,
                width: `${zone.largeur * 100}%`,
                height: `${zone.hauteur * 100}%`,
              }}
            >
              <span className="fc-masquage-image__marque" aria-hidden="true">
                ?
              </span>
            </div>
          );
        })}
      </div>
    </figure>
  );
}

export function MasquageTableau({
  contenu,
  mode,
  face,
  caseDemandee,
}: {
  contenu: ContenuTableau;
  mode: ModeMasquage;
  face: 'recto' | 'verso';
  caseDemandee: string | null;
}) {
  const [entetes, ...lignes] = contenu.lignes;
  return (
    <div className="fc-masquage-tableau">
      <table className="fc-masquage-tableau__table">
        {entetes && (
          <thead>
            <tr>
              {entetes.map((c) => (
                <th key={c.id} scope="col">
                  {rendreEnLigneMarkdown(c.contenu)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {lignes.map((ligne, i) => (
            <tr key={ligne[0]?.id ?? i}>
              {ligne.map((c) => {
                if (!c.grisee) return <td key={c.id}>{rendreEnLigneMarkdown(c.contenu)}</td>;
                const etat = etatMasquage(mode, face, c.id === caseDemandee);
                return (
                  <td
                    key={c.id}
                    className="fc-masquage-tableau__case"
                    data-etat={etat ?? undefined}
                  >
                    <span className="fc-masquage-tableau__contenu">
                      {rendreEnLigneMarkdown(c.contenu)}
                    </span>
                    {(etat === 'masque' || etat === 'demande') && (
                      <>
                        <span className="fc-masquage-tableau__marque" aria-hidden="true">
                          ?
                        </span>
                        <span className="fc-sr">
                          <span>{etat === 'demande' ? 'case demandée' : 'case masquée'}</span>
                        </span>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
